import { useEffect, useState } from 'react'
import { api } from '../api'
import { Card, Button, Form, Input, Select, Switch, Tag, Empty, Spin, Space, Typography, Drawer, message, Popconfirm } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, MailOutlined, LockOutlined, CrownOutlined } from '@ant-design/icons'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography

const ROLES = [
  { value: 'admin', label: 'Administrador', color: 'red' },
  { value: 'tecnico', label: 'Técnico', color: 'blue' },
  { value: 'operario', label: 'Operario', color: 'green' },
  { value: 'usuario', label: 'Usuario', color: 'default' },
]

export default function Usuarios() {
  const isMobile = useIsMobile()
  const [usuarios, setUsuarios] = useState([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const data = await api('/usuarios')
      setUsuarios(data)
    } catch (err) {
      console.error('Error cargando usuarios:', err)
      if (err.message?.includes('permisos')) message.error('Solo el administrador puede gestionar usuarios')
    }
    setCargando(false)
  }

  const abrirNuevo = () => {
    setEditando(null)
    form.resetFields()
    form.setFieldsValue({ rol: 'usuario', activo: true })
    setMostrarForm(true)
  }

  const abrirEdicion = (usuario) => {
    setEditando(usuario)
    form.setFieldsValue({
      nombre: usuario.nombre,
      email: usuario.email,
      rol: usuario.rol,
      activo: usuario.activo,
    })
    setMostrarForm(true)
  }

  const cerrarForm = () => {
    setMostrarForm(false)
    setEditando(null)
    form.resetFields()
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      const datos = { ...values }
      if (editando && !datos.password) delete datos.password

      if (editando) {
        await api(`/usuarios/${editando.id}`, { method: 'PUT', body: JSON.stringify(datos) })
        message.success('Usuario actualizado')
      } else {
        if (!datos.password) { message.error('La contraseña es obligatoria'); setGuardando(false); return }
        await api('/usuarios', { method: 'POST', body: JSON.stringify(datos) })
        message.success('Usuario creado')
      }
      cerrarForm()
      cargar()
    } catch (err) {
      message.error(err.message || 'Error guardando usuario')
    }
    setGuardando(false)
  }

  const desactivar = async (id) => {
    try {
      await api(`/usuarios/${id}`, { method: 'DELETE' })
      message.success('Usuario desactivado')
      cargar()
    } catch (err) {
      message.error(err.message || 'Error desactivando usuario')
    }
  }

  const getRolInfo = (rol) => ROLES.find(r => r.value === rol) || ROLES[3]

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Usuarios</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32', flexShrink: 0 }}>
          {!isMobile && 'Nuevo'}
        </Button>
      </div>

      <Drawer
        title={editando ? 'Editar usuario' : 'Nuevo usuario'}
        open={mostrarForm}
        onClose={cerrarForm}
        width={isMobile ? '100%' : 380}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={cerrarForm}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>
              {editando ? 'Actualizar' : 'Crear usuario'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Nombre" name="nombre" rules={[{ required: true, message: 'Obligatorio' }]}>
            <Input prefix={<UserOutlined />} placeholder="Nombre completo" />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ required: true, message: 'Obligatorio' }, { type: 'email', message: 'Email no válido' }]}>
            <Input prefix={<MailOutlined />} placeholder="correo@ejemplo.com" />
          </Form.Item>
          <Form.Item label={editando ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'} name="password" rules={editando ? [] : [{ required: true, message: 'Obligatorio' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder={editando ? 'Sin cambios' : 'Contraseña'} />
          </Form.Item>
          <Form.Item label="Rol" name="rol">
            <Select>
              {ROLES.map(r => (
                <Select.Option key={r.value} value={r.value}>
                  {r.value === 'admin' && <CrownOutlined style={{ marginRight: '4px' }} />}
                  {r.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {editando && (
            <Form.Item label="Activo" name="activo" valuePropName="checked">
              <Switch checkedChildren="Sí" unCheckedChildren="No" />
            </Form.Item>
          )}
        </Form>
      </Drawer>

      <div style={{ padding: '16px' }}>
        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : usuarios.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">No se pudieron cargar los usuarios</Text>}
            style={{ marginTop: '60px' }}
          />
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {usuarios.map(u => {
              const rolInfo = getRolInfo(u.rol)
              return (
                <Card key={u.id} size="small" hoverable style={{
                  borderRadius: '12px',
                  borderLeft: `3px solid ${u.activo ? '#52c41a' : '#ff4d4f'}`,
                  opacity: u.activo ? 1 : 0.6,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text strong style={{ fontSize: '15px', color: '#1B3A6B' }}>{u.nombre}</Text>
                        <Tag color={rolInfo.color} style={{ fontSize: '11px' }}>
                          {u.rol === 'admin' && <CrownOutlined style={{ marginRight: '2px' }} />}
                          {rolInfo.label}
                        </Tag>
                        {!u.activo && <Tag color="error" style={{ fontSize: '10px' }}>Inactivo</Tag>}
                      </div>
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        <MailOutlined /> {u.email}
                      </Text>
                    </div>
                    <Space size={4}>
                      <Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirEdicion(u)} />
                      {u.activo && (
                        <Popconfirm title="¿Desactivar este usuario?" onConfirm={() => desactivar(u.id)} okText="Sí" cancelText="No">
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                </Card>
              )
            })}
          </Space>
        )}
      </div>
    </div>
  )
}
